import Guid from "_shared/guids/guid";
import { personPermissionsPath } from "permissions/_views/people/person-permissions-path";
import { recordPermissionsPath, type RecordDomain } from "permissions/_views/records/record-permissions-path";
import { NEW_TEMPLATE_PATH, TEMPLATES_PATH } from "permissions/_views/templates/templates-paths";
import { ResourceType } from "permissions/resources/permission-resource-types";

/**
 * Where an address of the retired `/permissions?…` screens leads (#6177).
 *
 * Before the person, record, kind and templates pages, `/permissions` drew one of six screens chosen
 * by its query string: an overview of one person, the matrix, the details browser, the templates list
 * and the template editor, and the home page for an empty query. Those screens are gone, but their
 * addresses are in bookmarks, notification emails and the assistant's old answers, so each one is
 * sent to the page that now answers the same question:
 *
 * | Old address | New page |
 * | --- | --- |
 * | `?action=manage-templates` | `/permissions/templates` |
 * | `?action=create-template` | `/permissions/templates/new` |
 * | `?granteeUserId=X`, with any `action`, `view` or filter | `/permissions/people/X` |
 * | `?view=details&granteeUserId=X&resource=T` | `/permissions/people/X?records=T`, the kind's list opened |
 * | `?granteeUserIds=X`, one person | `/permissions/people/X` |
 * | `?forumIds=X`, `?documentIds=X`, `?proposalIds=X`, `?workAgreementIds=X`, one record | `/permissions/records/{domain}/X` |
 * | `?accessType=T` or `?resource=T`, one resource type | `/permissions/kinds/T`, which names the kind by its slug |
 * | Any other key the old screens owned: `view=details`, `action=manage-general`, several people or records, `categories`, `hasParent`, dates, parents, sorting and paging | `/permissions`, the home page, with those keys dropped |
 *
 * The matrix compared several people side by side. The design dropped that comparison: the kind page
 * lists everyone holding one kind, so several people lead home rather than to one of them.
 */

/** Every query key the retired screens read. A key not listed here is somebody else's and is kept. */
export const OLD_PERMISSIONS_KEYS: readonly string[] = [
  "action",
  "view",
  "granteeUserId",
  "granteeUserIds",
  "templateName",
  "hasParent",
  "category",
  "categories",
  "accessType",
  "childAccessTypes",
  "resource",
  "documentIds",
  "forumIds",
  "proposalIds",
  "workAgreementIds",
  "grantorUserId",
  "parentId",
  "parentResource",
  "legalEntityId",
  "showDisabled",
  "minCreatedDate",
  "maxCreatedDate",
  "minDisabledDate",
  "maxDisabledDate",
  "name",
  "sortBy",
  "sortDir",
  "pageIndex",
  "pageSize",
];

/** The id-list filter each record domain's old address used. */
const OLD_RECORD_FILTERS: Readonly<Record<string, RecordDomain>> = {
  forumIds: "forums",
  documentIds: "documents",
  proposalIds: "proposals",
  workAgreementIds: "work-agreements",
};

const RESOURCE_TYPES: ReadonlySet<string> = new Set(Object.values(ResourceType));

function guids(params: URLSearchParams, name: string): string[] {
  const values = params
    .getAll(name)
    .map((value) => value.trim().toLowerCase())
    .filter((value) => Guid.isValid(value));
  return [...new Set(values)];
}

/**
 * A resource type the old filters named, such as `ForumAccess`. The details browser wrote it as
 * `accessType`, sometimes without the `Access` suffix or with spaces ("Document"); the overview's chips
 * wrote it as `resource`, which could also hold a JSON filter that names no single type.
 */
function resourceType(params: URLSearchParams, name: string): string | undefined {
  const value = params.get(name)?.replaceAll(" ", "");
  if (!value) return undefined;
  const typed = value.endsWith("Access") ? value : `${value}Access`;
  return [...RESOURCE_TYPES].find((candidate) => candidate.toLowerCase() === typed.toLowerCase());
}

/** The new page an address of the retired screens meant, or nothing for the home page's own address. */
export function oldPermissionsAddressRedirect(search: string): string | undefined {
  const params = new URLSearchParams(search);

  const action = params.get("action");
  if (action === "manage-templates") return TEMPLATES_PATH;
  if (action === "create-template") return NEW_TEMPLATE_PATH;

  const [person, ...otherPeople] = [...guids(params, "granteeUserId"), ...guids(params, "granteeUserIds")];
  if (person && otherPeople.every((other) => other === person)) {
    return personPermissionsPath(new Guid(person), resourceType(params, "resource"));
  }

  const records = Object.entries(OLD_RECORD_FILTERS).flatMap(([key, domain]) => guids(params, key).map((id) => ({ domain, id })));
  if (!person && records.length === 1) return recordPermissionsPath(records[0].domain, records[0].id);

  const kind = resourceType(params, "accessType") ?? resourceType(params, "resource");
  if (!person && records.length === 0 && kind) return `/permissions/kinds/${kind}`;

  if (!OLD_PERMISSIONS_KEYS.some((key) => params.has(key))) return undefined;
  for (const key of OLD_PERMISSIONS_KEYS) params.delete(key);
  const rest = params.toString();
  return rest ? `/permissions?${rest}` : "/permissions";
}
